import styled from 'styled-components';

export const Page = styled.div`
    --ink: #19213d;
    --muted: #6d748c;
    --line: #e7eaf2;
    --accent: #6255e7;
    --accent-soft: #efedff;

    display: flex;
    flex-direction: column;
    gap: 20px;
    color: var(--ink);
`;

export const Header = styled.header`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 20px;

    @media (max-width: 900px) {
        align-items: flex-start;
        flex-direction: column;
    }
`;

export const Eyebrow = styled.div`
    margin-bottom: 7px;
    color: #6255e7;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.08em;
`;

export const Title = styled.h1`
    margin: 0;
    font-size: clamp(28px, 3vw, 38px);
    line-height: 1.25;
    letter-spacing: -0.04em;
`;

export const Description = styled.p`
    margin: 8px 0 0;
    color: var(--muted);
    font-size: 15px;
    line-height: 1.6;
`;

export const PeriodPanel = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px;
    border: 1px solid var(--line);
    border-radius: 14px;
    background: #fff;
    box-shadow: 0 6px 24px rgba(27, 34, 62, 0.05);
`;

export const QuickButton = styled.button`
    height: 36px;
    padding: 0 13px;
    border: 0;
    border-radius: 9px;
    background: ${(props) => (props.$active ? 'var(--accent)' : '#f4f5f8')};
    color: ${(props) => (props.$active ? '#fff' : '#51586f')};
    font-weight: 700;
    cursor: pointer;
`;

export const DateInput = styled.input`
    height: 36px;
    padding: 0 10px;
    border: 1px solid var(--line);
    border-radius: 9px;
    color: var(--ink);
    font: inherit;
`;

export const ApplyButton = styled.button`
    height: 36px;
    padding: 0 16px;
    border: 0;
    border-radius: 9px;
    background: #20263b;
    color: #fff;
    font-weight: 700;
    cursor: pointer;
`;

export const Notice = styled.div`
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 12px 15px;
    border: 1px solid #dce5ff;
    border-radius: 12px;
    background: #f5f8ff;
    color: #4d5d85;
    font-size: 13px;
    line-height: 1.5;
`;

export const KpiGrid = styled.section`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px;

    @media (max-width: 1100px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

export const KpiCard = styled.article`
    position: relative;
    overflow: hidden;
    min-height: 124px;
    padding: 20px;
    border: 1px solid var(--line);
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 8px 28px rgba(31, 38, 65, 0.05);

    &::after {
        content: '';
        position: absolute;
        right: -18px;
        bottom: -24px;
        width: 76px;
        height: 76px;
        border-radius: 50%;
        background: ${(props) => props.$tint || '#efedff'};
    }
`;

export const KpiLabel = styled.div`
    color: var(--muted);
    font-size: 13px;
    font-weight: 600;
`;

export const KpiValue = styled.div`
    margin-top: 13px;
    font-size: 30px;
    font-weight: 800;
    letter-spacing: -0.04em;
`;

export const KpiHint = styled.div`
    margin-top: 6px;
    color: #9298a9;
    font-size: 12px;
`;

export const InsightStrip = styled.section`
    display: grid;
    grid-template-columns: 1.1fr 1fr 1fr;
    gap: 1px;
    overflow: hidden;
    border: 1px solid var(--line);
    border-radius: 16px;
    background: var(--line);

    @media (max-width: 850px) {
        grid-template-columns: 1fr;
    }
`;

export const Insight = styled.div`
    padding: 18px 20px;
    background: #fff;
    color: var(--muted);
    font-size: 14px;
    line-height: 1.6;

    strong {
        display: block;
        margin-top: 4px;
        color: var(--ink);
        font-size: 17px;
    }
`;

export const DwellSection = styled.section`
    padding: 22px;
    border: 1px solid var(--line);
    border-radius: 18px;
    background: #fff;
    box-shadow: 0 10px 32px rgba(31, 38, 65, 0.05);
`;

export const DwellSample = styled.div`
    flex: 0 0 auto;
    padding: 10px 14px;
    border-radius: 12px;
    background: #e8f7f7;
    color: #66747b;
    font-size: 12px;

    strong {
        display: block;
        margin-top: 2px;
        color: #207c78;
        font-size: 22px;
    }
`;

export const DwellSummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;

    @media (max-width: 850px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 520px) {
        grid-template-columns: 1fr;
    }
`;

export const DwellSummary = styled.div`
    padding: 15px;
    border-radius: 13px;
    background: #f3f7f7;

    span {
        display: block;
        color: #70778c;
        font-size: 11px;
        font-weight: 700;
    }

    strong {
        display: block;
        margin-top: 7px;
        color: #252c45;
        font-size: 22px;
    }

    small {
        display: block;
        margin-top: 4px;
        color: #9298a9;
        font-size: 10px;
    }
`;

export const DwellDistributionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 11px;
    margin-top: 20px;
`;

export const DwellBucketRow = styled.div`
    display: grid;
    grid-template-columns: 92px minmax(0, 1fr) 112px;
    align-items: center;
    gap: 12px;

    @media (max-width: 520px) {
        grid-template-columns: 78px minmax(0, 1fr) 86px;
        gap: 8px;
    }
`;

export const DwellBucketLabel = styled.span`
    color: #555d76;
    font-size: 12px;
    font-weight: 700;
`;

export const DwellBar = styled.div`
    overflow: hidden;
    height: 9px;
    border-radius: 999px;
    background: #eef1f3;

    span {
        display: block;
        width: ${(props) => `${Math.max(props.$value || 0, 1)}%`};
        height: 100%;
        border-radius: inherit;
        background: linear-gradient(90deg, #2aa19a, #72c9c4);
    }
`;

export const DwellBucketValue = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 7px;
    font-size: 12px;

    strong {
        color: #2b334c;
    }

    span {
        min-width: 40px;
        color: #858b9d;
        text-align: right;
    }
`;

export const VisitDwellSummary = styled.div`
    margin-top: 18px;
    padding: 13px 15px;
    border-radius: 12px;
    background: #f5f8ff;
    color: #65708e;
    font-size: 12px;
    line-height: 1.6;

    strong {
        color: #313b5c;
    }
`;

export const FunnelSection = styled.section`
    padding: 22px;
    border: 1px solid var(--line);
    border-radius: 18px;
    background: linear-gradient(145deg, #ffffff 0%, #f8f8ff 100%);
    box-shadow: 0 10px 32px rgba(31, 38, 65, 0.05);
`;

export const SectionHeading = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 18px;

    @media (max-width: 700px) {
        flex-direction: column;
    }
`;

export const FunnelTotal = styled.div`
    flex: 0 0 auto;
    padding: 10px 14px;
    border-radius: 12px;
    background: #efedff;
    color: #686f86;
    font-size: 12px;

    strong {
        display: block;
        margin-top: 2px;
        color: #5146ce;
        font-size: 22px;
    }
`;

export const FunnelSteps = styled.div`
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 10px;

    @media (max-width: 1050px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 580px) {
        grid-template-columns: 1fr;
    }
`;

export const FunnelStep = styled.article`
    position: relative;
    min-height: 148px;
    padding: 15px;
    border: 1px solid #e6e4fb;
    border-radius: 14px;
    background: #fff;
`;

export const FunnelStepIndex = styled.span`
    display: grid;
    width: 25px;
    height: 25px;
    place-items: center;
    border-radius: 8px;
    background: #efedff;
    color: #6255e7;
    font-size: 12px;
    font-weight: 800;
`;

export const FunnelStepName = styled.div`
    margin-top: 11px;
    color: #646b81;
    font-size: 12px;
    font-weight: 700;
`;

export const FunnelStepValue = styled.strong`
    display: block;
    margin-top: 4px;
    font-size: 25px;
    letter-spacing: -0.04em;
`;

export const FunnelStepMeta = styled.div`
    margin-top: 4px;
    color: #8c92a4;
    font-size: 11px;
`;

export const DropOff = styled.span`
    position: absolute;
    right: 12px;
    top: 12px;
    padding: 4px 7px;
    border-radius: 999px;
    background: ${(props) => (props.$warning ? '#fff0ee' : '#f3f4f7')};
    color: ${(props) => (props.$warning ? '#d45147' : '#73798b')};
    font-size: 10px;
    font-weight: 700;
`;

export const FunnelEmpty = styled.div`
    padding: 28px 18px;
    border: 1px dashed #d9d6f4;
    border-radius: 14px;
    background: #fff;
    color: #757c91;
    text-align: center;
    font-size: 13px;
`;

export const DetailInsightGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-top: 14px;

    @media (max-width: 850px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 520px) {
        grid-template-columns: 1fr;
    }
`;

export const DetailInsight = styled.div`
    padding: 15px;
    border-radius: 13px;
    background: #f3f5fa;

    span {
        display: block;
        color: #70778c;
        font-size: 11px;
        font-weight: 700;
    }

    strong {
        display: block;
        margin-top: 7px;
        color: #252c45;
        font-size: 22px;
    }

    small {
        display: block;
        margin-top: 4px;
        color: #9298a9;
        font-size: 10px;
    }
`;

export const TwoColumn = styled.section`
    display: grid;
    grid-template-columns: minmax(0, 1.45fr) minmax(320px, 0.85fr);
    gap: 16px;

    @media (max-width: 1050px) {
        grid-template-columns: 1fr;
    }
`;

export const Card = styled.article`
    min-width: 0;
    padding: 20px;
    border: 1px solid var(--line);
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 8px 28px rgba(31, 38, 65, 0.04);
`;

export const CardHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 18px;
`;

export const CardTitle = styled.h2`
    margin: 0;
    font-size: 18px;
    letter-spacing: -0.02em;
`;

export const CardHelp = styled.p`
    margin: 5px 0 0;
    color: var(--muted);
    font-size: 12px;
    line-height: 1.5;
`;

export const ChartBox = styled.div`
    width: 100%;
    height: ${(props) => props.$height || '290px'};
`;

export const Legend = styled.div`
    display: flex;
    gap: 14px;
    color: var(--muted);
    font-size: 12px;

    span::before {
        content: '';
        display: inline-block;
        width: 8px;
        height: 8px;
        margin-right: 6px;
        border-radius: 50%;
        background: ${(props) => props.color || '#6255e7'};
    }
`;

export const RankList = styled.ol`
    display: flex;
    flex-direction: column;
    gap: 13px;
    margin: 0;
    padding: 0;
    list-style: none;
`;

export const RankItem = styled.li`
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
`;

export const RankNumber = styled.span`
    display: grid;
    width: 26px;
    height: 26px;
    place-items: center;
    border-radius: 8px;
    background: var(--accent-soft);
    color: var(--accent);
    font-size: 12px;
    font-weight: 800;
`;

export const RankName = styled.div`
    min-width: 0;
    font-size: 14px;
    font-weight: 700;
`;

export const RankMeta = styled.div`
    margin-top: 3px;
    color: var(--muted);
    font-size: 11px;
`;

export const RankCount = styled.strong`
    font-size: 14px;
`;

export const Progress = styled.div`
    grid-column: 2 / 4;
    overflow: hidden;
    height: 5px;
    border-radius: 999px;
    background: #f0f1f5;

    span {
        display: block;
        width: ${(props) => `${Math.max(2, props.$value || 0)}%`};
        height: 100%;
        border-radius: inherit;
        background: linear-gradient(90deg, #6255e7, #8a7ff2);
    }
`;

export const FlowGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;

    @media (max-width: 800px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

export const FlowItem = styled.div`
    padding: 16px;
    border-radius: 13px;
    background: ${(props) => props.$background || '#f7f6ff'};
`;

export const FlowName = styled.div`
    color: var(--muted);
    font-size: 12px;
`;

export const FlowValue = styled.div`
    margin-top: 8px;
    font-size: 22px;
    font-weight: 800;
`;

export const SegmentRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
`;

export const SegmentChip = styled.span`
    padding: 8px 11px;
    border: 1px solid var(--line);
    border-radius: 999px;
    background: #fafbfc;
    color: #555d76;
    font-size: 12px;

    strong {
        margin-left: 5px;
        color: var(--ink);
    }
`;

export const StateCard = styled.div`
    display: grid;
    min-height: 360px;
    padding: 30px;
    place-items: center;
    border: 1px solid var(--line);
    border-radius: 18px;
    background: #fff;
    color: var(--muted);
    text-align: center;

    h2 {
        margin: 0 0 8px;
        color: var(--ink);
    }

    p {
        max-width: 520px;
        margin: 0;
        line-height: 1.7;
    }

    button {
        margin-top: 16px;
        padding: 9px 14px;
        border: 0;
        border-radius: 9px;
        background: var(--accent);
        color: #fff;
        cursor: pointer;
    }
`;

export const ProductTabs = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: 6px;
    gap: 6px;
    background: #e9ebf3;
    border-radius: 16px;
`;
export const ProductTab = styled.button`
    min-height: 66px;
    border: 0;
    border-radius: 12px;
    font-size: 21px;
    font-weight: 800;
    color: ${(p) => (p.$active ? '#5146ce' : '#747b91')};
    background: ${(p) => (p.$active ? '#fff' : 'transparent')};
    box-shadow: ${(p) => (p.$active ? '0 3px 12px #24295112' : 'none')};
    cursor: pointer;
    &:focus-visible {
        outline: 3px solid #6255e7;
        outline-offset: 1px;
    }
`;
export const TabPanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;
export const WelcomeContent = styled(TabPanel)``;
export const FunnelTableWrap = styled.div`
    overflow-x: auto;
    margin-top: 20px;
`;
export const FunnelTable = styled.table`
    width: 100%;
    min-width: 690px;
    border-collapse: collapse;
    font-size: 13px;
    th,
    td {
        padding: 18px 12px;
        text-align: right;
        border-bottom: 1px solid #eceef4;
        white-space: nowrap;
    }
    th:first-child {
        text-align: left;
    }
    thead {
        color: #73798b;
        background: #f5f5fb;
        font-size: 12px;
    }
    tbody th {
        font-weight: 700;
    }
    td small {
        display: inline-block;
        margin-top: 6px;
        color: #747b91;
    }
`;
export const StageLabel = styled.span`
    display: flex;
    align-items: center;
    gap: 10px;
`;
export const ReachBar = styled.div`
    width: 100px;
    height: 7px;
    margin-left: auto;
    overflow: hidden;
    background: #e9e7f7;
    border-radius: 4px;
    span {
        display: block;
        width: ${(p) => p.$value}%;
        height: 100%;
        background: #7467e7;
        border-radius: inherit;
    }
`;
export const DropRate = styled.span`
    color: ${(p) => (p.$warning ? '#c54d3e' : '#747b91')};
    font-weight: 700;
`;
export const MeasurementList = styled.ul`
    padding-left: 20px;
    margin-bottom: 0;
    color: #73798b;
    font-size: 13px;
    line-height: 1.8;
    li + li {
        margin-top: 6px;
    }
`;
